import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { GetIrlEventByDispensaryDocument, GetIrlEventDocument, IrlEvent, IrlEventTrackingEvent } from 'src/gql/graphql';
import { useIrlEventTracking } from './use-irl-event-tracking';
import { useIrlEventParams } from './use-irl-event-params';
import { IRL_EVENT_ACTIVE_ONLY_AVAILABILITY_FLAG } from '../flags';

type UseIrlEventReturn = {
  irlEvent: IrlEvent | null;
  loading: boolean;
  error: Error | null;
};

/**
 * Hook for fetching IRL event data and tracking the 'event open' event.
 */
export function useIrlEvent(): UseIrlEventReturn {
  const flags = useFlags();
  const {
    irlEventId,
    enterpriseId,
    isoRegionCode,
    dispensaryId,
    hasEnterpriseParams,
    hasDispensaryParam,
  } = useIrlEventParams();
  const [hasTrackedOpen, setHasTrackedOpen] = useState(false);
  const { trackEvent } = useIrlEventTracking();
  const fetchPolicy = flags[IRL_EVENT_ACTIVE_ONLY_AVAILABILITY_FLAG] === true ? 'cache-and-network' : 'cache-first';

  const { data: enterpriseData, loading: enterpriseLoading, error: enterpriseError = null } = useQuery(
    GetIrlEventDocument,
    {
      variables: { irlEventId, enterpriseId, isoRegionCode },
      skip: !irlEventId || !hasEnterpriseParams,
      fetchPolicy,
    }
  );

  const { data: dispensaryData, loading: dispensaryLoading, error: dispensaryError = null } = useQuery(
    GetIrlEventByDispensaryDocument,
    {
      variables: { irlEventId, dispensaryId },
      skip: !irlEventId || !hasDispensaryParam,
      fetchPolicy,
    }
  );

  const irlEvent = (hasDispensaryParam ? dispensaryData?.getIrlEventByDispensary : enterpriseData?.getIrlEvent) ?? null;
  const loading = hasDispensaryParam ? dispensaryLoading : enterpriseLoading;
  const error = hasDispensaryParam ? dispensaryError : enterpriseError;

  // Track open event once when event data loads
  useEffect(() => {
    if (irlEvent && !hasTrackedOpen) {
      setHasTrackedOpen(true);
      void trackEvent(IrlEventTrackingEvent.open);
    }
  }, [irlEvent, hasTrackedOpen, trackEvent]);

  return {
    irlEvent,
    loading,
    error,
  };
}
